import {
  tenantBillingControllerGetSubscription,
  tenantBillingControllerListPayments,
} from '@/generated/korza-api-client';
import type {
  TenantPaymentResponseDto,
  TenantSubscriptionResponseDto,
} from '@/generated/korza-api-client/types.gen';
import { useQuery } from '@tanstack/react-query';
import { useTenant } from '@/hooks/use-tenant';

export type BillingApiData = {
  subscription: TenantSubscriptionResponseDto | null;
  payments: TenantPaymentResponseDto[];
};

async function fetchBillingData(tenantSlug: string): Promise<BillingApiData> {
  const subscriptionResult = await tenantBillingControllerGetSubscription({
    path: { tenantSlug },
  });

  if (subscriptionResult.response.status === 404) {
    return { subscription: null, payments: [] };
  }

  if (subscriptionResult.error || !subscriptionResult.data) {
    throw new Error('Failed to load billing subscription');
  }

  const subscription = subscriptionResult.data;

  const paymentsResponse = await tenantBillingControllerListPayments({
    path: { tenantSlug },
    throwOnError: true,
  });

  return {
    subscription,
    payments: paymentsResponse.data ?? [],
  };
}

export function useActiveSubscription() {
  const tenant = useTenant();

  return useQuery({
    queryKey: ['billing', tenant.slug],
    queryFn: () => fetchBillingData(tenant.slug),
    select: (data) => data.subscription,
    enabled: !!tenant.slug,
  });
}

export function useBilling() {
  const tenant = useTenant();

  return useQuery<BillingApiData>({
    queryKey: ['billing', tenant.slug],
    queryFn: () => fetchBillingData(tenant.slug),
    enabled: !!tenant.slug,
  });
}
